import React, { createContext, useEffect, useMemo, useState } from "react";

import * as Sentry from "@sentry/react";
import {
  UseMutateFunction,
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { compact, findIndex, isEmpty, map, merge, noop } from "lodash/fp";
import { useNavigate } from "react-router-dom";

import {
  deleteWorkspace,
  deleteWorkspaceUser,
  deleteWorkspaceUsers,
  EnterpriseUserBody,
  getWorkspaceInvites,
  migrateWorkspaceUserMeetings,
  PatchEnterpriseUserBody,
  updateAllWorkspaceInvites,
  updateWorkspaceName,
  updateWorkspaceUser,
} from "@app/common/account/api";
import apiClient from "@app/common/api/apiClient";
import useAuth from "@app/common/auth/useAuth";
import { showToast } from "@app/common/hooks/useToasts";
import ROUTES from "@app/common/routes";
import { pluralize } from "@app/common/utils";
import {
  createTeam,
  createTeamUser,
  deleteCustomLogo,
  deleteTeam,
  deleteTeamUser,
  patchTeamUser,
  updateTeamName,
  updateTeamSettings,
  uploadCustomLogo,
} from "@app/common/workspaces/api";
import * as Events from "@app/common/workspaces/events";
import {
  AdminEnterpriseWorkspace,
  EnterpriseTeam,
  EnterpriseTeamSettings,
  EnterpriseWorkspace,
  EnterpriseWorkspaceSettingUpdateConfig,
  EnterpriseWorkspaceTeam,
  EnterpriseWorkspaceUser,
} from "@app/common/workspaces/types";
import {
  findMyRoleInTeam,
  populateWorkspace,
} from "@app/common/workspaces/workspaceUtils";
import { replaceAt } from "@app/utils/arrays";

export type UpdateTeamUserBody = EnterpriseUserBody & { teamId: string };

interface UpdateTeamNameBody {
  teamId: string;
  name: string;
}
interface UpdateTeamSettingsBody {
  teamId: string;
  settings: Partial<EnterpriseTeamSettings>;
}

interface UpdateWorkspaceNameContext {
  nameToUpdate: string;
  previousName: string;
}

interface UpdateWorkspaceNameBody {
  name: string;
}

interface UpdateTeamContext {
  teamBeforeUpdate: EnterpriseTeam | EnterpriseWorkspaceTeam;
}

type LoadedTeamMap = { [key: string]: EnterpriseTeam };
export interface UploadLogoBody {
  file: File;
  onSuccess: () => void;
}

interface CommonWorkspaceContext {
  addOrUpdateTeamUsers: UseMutateFunction<
    boolean,
    unknown,
    { usersToCreate: UpdateTeamUserBody[]; usersToUpdate: UpdateTeamUserBody[] }
  >;
  createTeam: UseMutateFunction<EnterpriseTeam, unknown, string>;
  deleteTeam: UseMutationResult<void, unknown, { id: string }>;
  deleteWorkspace: UseMutationResult<void, unknown, void>;
  migrateMeetings: UseMutationResult<void, unknown, string>;
  inWorkspace: boolean;
  leaveWorkspace: UseMutationResult<void, unknown, void>;
  loadTeam: (team: EnterpriseTeam) => void;
  loadedTeams: LoadedTeamMap;
  removeUsersFromTeam: UseMutationResult<
    string[], // user ids that failed to be removed
    unknown,
    { teamId: string; userIds: string[] }
  >;
  removeUsersFromWorkspace: UseMutateFunction<
    string[], // user or invite ids that failed to be removed
    unknown,
    { userIds: string[]; inviteIds: string[] }
  >;
  setWorkspaceId: (id: string) => void;
  updateTeamName: UseMutateFunction<void, unknown, UpdateTeamNameBody>;
  updateTeamSettings: UseMutateFunction<void, unknown, UpdateTeamSettingsBody>;
  updateTeamUsers: UseMutateFunction<
    UpdateTeamUserBody[], // team users that failed to update
    unknown,
    UpdateTeamUserBody[]
  >;
  updateWorkspaceName: UseMutateFunction<
    void,
    unknown,
    UpdateWorkspaceNameBody
  >;
  updateWorkspaceUser: UseMutateFunction<
    void,
    unknown,
    PatchEnterpriseUserBody
  >;
  uploadCustomBotLogo: UseMutateFunction<void, unknown, UploadLogoBody>;
  deleteCustomBotLogo: UseMutateFunction<void>;
  workspaceIsInactive: boolean;
  workspaceId?: string;
  workspaceError: boolean;
  workspaceLoading: boolean;
  workspaceUser: EnterpriseWorkspaceUser;
}

interface WorkspaceContext extends CommonWorkspaceContext {
  workspace: EnterpriseWorkspace;
}

export interface AdminWorkspaceContext extends CommonWorkspaceContext {
  workspace: AdminEnterpriseWorkspace;
}

const DEFAULT_SETTING_UPDATE_CONFIG: EnterpriseWorkspaceSettingUpdateConfig = {
  view: "member",
  edit: "admin",
};

const DEFAULT_WORKSPACE_USER: EnterpriseWorkspaceUser = {
  email: "",
  firstName: "",
  fullName: "",
  initials: "",
  lastName: "",
  role: "member",
  teams: [],
  type: "user",
  userId: "0",
  workspaceId: "",
};

const DEFAULT_WORKSPACE: EnterpriseWorkspace = {
  id: "",
  name: "",
  invites: [],
  teams: [],
  users: [],
  settings: {
    allowMultiTeamMembership: true,
    affectiveMetricsEnabled: true,
    affectiveMetricsEnabledIsUserEditable: false,
    anyoneWithLinkAccess: true,
    botConfig: {
      display: false,
      meetingScore: true,
      talkTime: true,
      timer: true,
    },
    botNameMode: "read_ai_meeting_notes",
    botConfigIsUserEditable: true,
    botNameModeIsUserEditable: false,
    botNameModeCustomName: "read.ai meeting notes",
    calendarJoinInternalOnly: false,
    calendarPermissionLevel: "hosting_only",
    emailPreferences: [],
    externalParticipantAccessLevelDefault: "viewer_full",
    externalParticipantAccessLevelDefaultIsUserEditable: false,
    integrationGoogleCalendarEnabled: true,
    integrationGoogleEmailEnabled: true,
    integrationHubspotPullEnabled: true,
    integrationHubspotPushEnabled: true,
    integrationMicrosoftGraphCalendarEnabled: true,
    integrationMicrosoftGraphEmailEnabled: true,
    integrationMicrosoftGraphTeamsChatIngestEnabled: true,
    integrationMicrosoftGraphTeamsPushEnabled: true,
    integrationSalesforcePullEnabled: true,
    integrationSalesforcePushEnabled: true,
    integrationSlackBotEnabled: true,
    integrationSlackIngestEnabled: true,
    integrationWebhooksEnabled: true,
    integrationZapierEnabled: true,
    integrationZoomCalendarEnabled: true,
    internalParticipantAccessLevelDefault: "editor",
    internalParticipantAccessLevelDefaultIsUserEditable: false,
    meetingDownloadEnabled: true,
    nonCalendarIntegrationsEnabled: true,
    sendMeetingRecap: true,
    sendPreRead: true,
    sendTopicSummary: true,
    shareLinkInBotMessage: true,
    shareLinkInBotMessageIsUserEditable: true,
    shareTeamData: {
      edit: "manager",
      view: "manager",
    },
    storeRecording: DEFAULT_SETTING_UPDATE_CONFIG,
    storeRecordingOptIn: true,
    storeRecordingOptInIsUserEditable: true,
    teamRollups: {
      edit: "manager",
      view: "manager",
    },
    transcriptionOptIn: true,
    transcriptionOptInIsUserEditable: true,
    transcripts: DEFAULT_SETTING_UPDATE_CONFIG,
    useCustomBotLogo: false,
    useLeastPermissivePermissionResolution: false,
    workspaceInvitations: "manager",
    workspaceRollups: {
      edit: "admin",
      view: "admin",
    },
  },
  status: "active",
};

const DEFAULT_WORKSPACE_CONTEXT: WorkspaceContext = {
  addOrUpdateTeamUsers: noop,
  createTeam: noop,
  deleteTeam: {} as UseMutationResult<void, unknown, { id: string }>,
  deleteWorkspace: {} as UseMutationResult<void, unknown, void>,
  migrateMeetings: {} as UseMutationResult<void, unknown, string>,
  inWorkspace: false,
  leaveWorkspace: {} as UseMutationResult<void, unknown, void>,
  loadTeam: noop,
  loadedTeams: {},
  removeUsersFromTeam: {} as UseMutationResult<
    string[],
    unknown,
    { teamId: string; userIds: string[] }
  >,
  removeUsersFromWorkspace: noop,
  setWorkspaceId: noop,
  updateTeamName: noop,
  updateTeamSettings: noop,
  updateTeamUsers: noop,
  updateWorkspaceName: noop,
  updateWorkspaceUser: noop,
  uploadCustomBotLogo: noop,
  deleteCustomBotLogo: noop,
  workspace: DEFAULT_WORKSPACE,
  workspaceError: false,
  workspaceIsInactive: false,
  workspaceLoading: false,
  workspaceUser: DEFAULT_WORKSPACE_USER,
};

interface WorkspaceProviderProps {
  children: React.ReactNode;
  workspaceId?: string;
}

export const WorkspaceContext = createContext<WorkspaceContext>(
  DEFAULT_WORKSPACE_CONTEXT
);

const removeAllInvites = async (workspaceId: string, inviteIds: string[]) => {
  if (isEmpty(inviteIds)) return [];

  // if we're charging on invite create, we need to use the bulk endpoint so that the updates
  // can happen as a single transaction vs. many concurrent transactions (which will confuse Stripe!)
  const result = await updateAllWorkspaceInvites(workspaceId, {
    status: "removed",
    ids: inviteIds,
  });

  // ignore 404s since if we're trying to remove the invite, the invite not existing in
  // the first place is effectively the same thing
  return compact(
    (result.failures ?? []).map((f) => (f.errorCode === 404 ? null : f.id))
  );
};

const addUserToAllTeams = async (
  workspaceId: string,
  users: UpdateTeamUserBody[]
) => {
  if (isEmpty(users)) return [];

  const results = await Promise.allSettled(
    users.map(
      async (user) => await createTeamUser(workspaceId, user.teamId, user)
    )
  );

  return compact(
    results.map((result, i) => (result.status === "rejected" ? users[i] : null))
  );
};

const patchAllTeamUsers = async (
  workspaceId: string,
  users: UpdateTeamUserBody[]
) => {
  if (isEmpty(users)) return [];

  const results = await Promise.allSettled(
    users.map(
      async (user) => await patchTeamUser(workspaceId, user.teamId, user)
    )
  );

  return compact(
    results.map((result, i) => (result.status === "rejected" ? users[i] : null))
  );
};

const removeAllTeamUsers = async (
  workspaceId: string,
  teamId: string,
  userIds: string[]
) => {
  if (isEmpty(userIds)) return [];

  const results = await Promise.allSettled(
    userIds.map(
      async (userId) => await deleteTeamUser(workspaceId, teamId, userId)
    )
  );

  return compact(
    results.map((result, i) =>
      result.status === "rejected" ? userIds[i] : null
    )
  );
};

export const WorkspaceContextProvider = ({
  children,
  workspaceId,
}: WorkspaceProviderProps) => {
  const { user, fetchAndSetUser } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<
    string | undefined
  >(workspaceId);

  const [loadedTeams, setLoadedTeams] = useState<LoadedTeamMap>({});

  const workspaceQueryKey = ["workspace", currentWorkspaceId];

  useEffect(() => {
    if (workspaceId !== currentWorkspaceId) {
      setCurrentWorkspaceId(workspaceId);
    }
  }, [workspaceId]);

  const loadTeam = (team: EnterpriseTeam) =>
    setLoadedTeams({ ...loadedTeams, [team.id]: team });

  const workspaceQuery = useQuery({
    queryKey: workspaceQueryKey,
    queryFn: async () => {
      const [workspace, invites] = await Promise.all([
        apiClient.workspace.getWorkspace(currentWorkspaceId!),
        getWorkspaceInvites(currentWorkspaceId!),
      ]);
      return populateWorkspace(workspace, invites);
    },
    enabled: Boolean(currentWorkspaceId),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: 60 * 1000,
  });

  const workspace = workspaceQuery.data ?? DEFAULT_WORKSPACE;

  const createTeamMutation = useMutation({
    mutationFn: async (name: string) =>
      await createTeam(currentWorkspaceId!, name),
    onSettled: (data, error) => {
      if (error) {
        Sentry.captureException(error);
        return;
      }

      queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
    },
  });

  const migrateUserMeetingMutation = useMutation<void, unknown, string>({
    mutationFn: async (userId: string) =>
      migrateWorkspaceUserMeetings(currentWorkspaceId!, userId),
    onSuccess: () => {
      showToast({
        severity: "success",
        message: "Your meetings have been successfully migrated!",
      });
    },
    onError: (error) => {
      Sentry.captureException(error);
      showToast({
        severity: "error",
        message:
          "Something went wrong when trying to migrate your meetings. Please try again or refresh the page.",
      });
    },
  });

  const deleteTeamMutation = useMutation({
    mutationFn: async ({ id }: { id: string }) =>
      await deleteTeam(currentWorkspaceId!, id),
    onSuccess: (data, { id: teamId }) => {
      const teamName = workspace.teams.find((team) => team.id === teamId)?.name;
      queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
      navigate(ROUTES.workspaceSettingsById(currentWorkspaceId!));
      showToast({
        severity: "success",
        message: `Your Team, ${teamName}, has been deleted.`,
      });
    },
    onError: (error) => {
      Sentry.captureException(error);
      showToast({
        severity: "error",
        message:
          "Something went wrong when trying to delete the Team. Please try again or refresh the page.",
      });
    },
  });

  const removeUsersFromTeamMutation = useMutation({
    mutationFn: async ({
      teamId,
      userIds,
    }: {
      teamId: string;
      userIds: string[];
    }) => await removeAllTeamUsers(currentWorkspaceId!, teamId, userIds),
    onSettled: (data, error, { teamId, userIds }) => {
      const team = workspace.teams.find((team) => team.id === teamId)!;
      const userIsLeavingTeam = user && userIds.includes(user.id);

      if (isEmpty(data)) {
        showToast({
          severity: "success",
          message: userIsLeavingTeam
            ? `You have left ${team.name}`
            : `${userIds.length} ${pluralize(
                "user",
                userIds.length
              )} removed from ${team.name}`,
        });
      } else {
        showToast({
          severity: "error",
          message: `Something went wrong when trying to remove ${
            data!.length
          } ${pluralize("user", data!.length)} from ${
            team.name
          }. Please try again or refresh the page.`,
        });
      }

      // if any of the queries were successful, refetch the workspace
      if (data!.length !== userIds.length) {
        queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
      }
    },
  });

  const updateTeamUsersMutation = useMutation({
    mutationFn: async (users: UpdateTeamUserBody[]) =>
      await patchAllTeamUsers(currentWorkspaceId!, users),
    onSettled: (data, error) => {
      if (error) {
        Sentry.captureException(error);
        return;
      }

      queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
    },
  });

  const addOrUpdateTeamUsersMutation = useMutation({
    mutationFn: async ({
      usersToCreate,
      usersToUpdate,
    }: {
      usersToCreate: UpdateTeamUserBody[];
      usersToUpdate: UpdateTeamUserBody[];
    }) => {
      const failedCreates = await addUserToAllTeams(
        currentWorkspaceId!,
        usersToCreate
      );
      const failedUpdates = await patchAllTeamUsers(
        currentWorkspaceId!,
        usersToUpdate
      );

      return isEmpty(failedCreates) && isEmpty(failedUpdates);
    },
    onSettled: (isSuccess, error) => {
      if (error) {
        Sentry.captureException(error);
        return;
      }

      if (isSuccess) {
        showToast({
          severity: "success",
          message: "Permissions updated!",
        });
      }

      queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
    },
  });

  const deleteWorkspaceMutation: UseMutationResult<
    void,
    unknown,
    void,
    unknown
  > = useMutation({
    mutationFn: async () => {
      Events.deleteWorkspace({ workspaceId: workspace.id });
      await deleteWorkspace(workspace.id);
    },
    onSuccess: async () => {
      const workspaceName = workspace.name;
      setCurrentWorkspaceId(undefined);
      queryClient.removeQueries({ queryKey: workspaceQueryKey });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["billing"] }),
        fetchAndSetUser(), // refetch the user/billing account
      ]);
      navigate(ROUTES.USER_SETTINGS_DEFAULT_SECTION);
      showToast({
        severity: "success",
        message: `Your Workspace, ${workspaceName}, has been deleted. If you would like to reactivate a deleted Workspace please reach out to support@read.ai.`,
      });
    },
    onError: (error) => {
      Sentry.captureException(error);
      showToast({
        severity: "error",
        message:
          "Something went wrong when trying to delete the Workspace. Please try again or refresh the page.",
      });
    },
  });

  const removeWorkspaceUsersOrInvitesMutation = useMutation({
    mutationFn: async ({
      userIds,
      inviteIds,
    }: {
      userIds: string[];
      inviteIds: string[];
    }) => {
      let userRemoveFailures: string[] = [];

      try {
        await deleteWorkspaceUsers(currentWorkspaceId!, userIds);
      } catch (error) {
        userRemoveFailures = userIds;
      }

      const inviteRemoveFailures = await removeAllInvites(
        currentWorkspaceId!,
        inviteIds
      );

      return userRemoveFailures.concat(inviteRemoveFailures);
    },
    onSettled: async (failures, error, { userIds, inviteIds }) => {
      await fetchAndSetUser(); // refetch the user/billing account

      if (error) {
        Sentry.captureException(error);
        return;
      }

      const totalRemoved = userIds.length + inviteIds.length;

      if (isEmpty(failures)) {
        showToast({
          severity: "success",
          message: `${totalRemoved} ${pluralize(
            "member",
            totalRemoved
          )} removed from ${workspace.name}`,
        });
      } else {
        showToast({
          severity: "error",
          message:
            "Something went wrong when trying to remove the Workspace members. Please try again or refresh the page.",
        });
      }

      // if any of the queries were successful, refetch the workspace
      if (failures!.length !== totalRemoved) {
        queryClient.invalidateQueries({ queryKey: ["billing"] });
        queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
      }
    },
  });

  const leaveWorkspaceMutation = useMutation<void, unknown, void, unknown>({
    mutationFn: async () => deleteWorkspaceUser(currentWorkspaceId!, user!.id),
    onError: () => {
      showToast({
        severity: "error",
        message:
          "Something went wrong when trying to remove you from the Workspace. Please try again or refresh the page.",
      });
    },
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: workspaceQueryKey });
      setCurrentWorkspaceId(undefined);

      await Promise.all([
        fetchAndSetUser(),
        queryClient.invalidateQueries({ queryKey: ["billing"] }),
      ]);

      showToast({
        severity: "success",
        message: "Success! You have been removed from your Workspace.",
      });
    },
  });

  const updateWorkspaceNameMutation = useMutation({
    mutationFn: async ({ name: nameToUpdate }: UpdateWorkspaceNameBody) =>
      await updateWorkspaceName(currentWorkspaceId!, nameToUpdate),
    onMutate: async ({
      name: nameToUpdate,
    }: UpdateWorkspaceNameBody): Promise<UpdateWorkspaceNameContext> => {
      await queryClient.cancelQueries({ queryKey: workspaceQueryKey });
      const previousName = workspace.name;

      const newQueryData: EnterpriseWorkspace = {
        ...workspace,
        name: nameToUpdate,
      };

      queryClient.setQueryData(workspaceQueryKey, newQueryData);
      return { previousName, nameToUpdate };
    },
    onError: (error, _args, context) => {
      Sentry.captureException(error);
      showToast({
        severity: "error",
        message:
          "There was a problem updating your workspace name. Please try again or refresh the page.",
      });

      if (context?.previousName) {
        const previousQueryData = {
          ...workspace,
          name: context.previousName,
        };
        queryClient.setQueryData(workspaceQueryKey, previousQueryData);
      }
    },
  });

  const updateTeamNameMutation = useMutation({
    mutationFn: async ({ teamId, name }: UpdateTeamNameBody) => {
      return await updateTeamName(currentWorkspaceId!, teamId, name);
    },
    onMutate: async ({
      teamId,
      name,
    }: UpdateTeamNameBody): Promise<UpdateTeamContext | undefined> => {
      const teamQueryKey = ["workspaceTeam", teamId];

      await queryClient.cancelQueries({ queryKey: teamQueryKey });

      const teamIndex = findIndex({ id: teamId }, workspace.teams);

      const teamBeforeUpdate = workspace.teams[teamIndex];
      const newTeam = { ...teamBeforeUpdate, name };

      const workspaceTeams = replaceAt(workspace.teams, newTeam, teamIndex);
      const newWorkspace = { ...workspace, teams: workspaceTeams };

      queryClient.setQueryData(workspaceQueryKey, newWorkspace);

      return { teamBeforeUpdate };
    },
    onError: (error, updateTeamBody, context) => {
      Sentry.captureException(error);
      showToast({
        severity: "error",
        message:
          "There was an error changing the team name. Please try again or refresh the page.",
      });

      if (context?.teamBeforeUpdate) {
        queryClient.setQueryData(
          ["workspaceTeam", updateTeamBody.teamId],
          context.teamBeforeUpdate
        );
      }
    },
  });

  const updateTeamSettingMutation = useMutation({
    mutationFn: async (updateTeamBody: UpdateTeamSettingsBody) => {
      const { teamId, settings } = updateTeamBody;
      return await updateTeamSettings(currentWorkspaceId!, teamId, settings);
    },
    onMutate: async (
      updateTeamBody: UpdateTeamSettingsBody
    ): Promise<UpdateTeamContext | undefined> => {
      const teamQueryKey = ["workspaceTeam", updateTeamBody.teamId];

      await queryClient.cancelQueries({ queryKey: teamQueryKey });
      const teamBeforeUpdate = loadedTeams[updateTeamBody.teamId];
      if (!teamBeforeUpdate) return;

      const newTeam = merge({ ...teamBeforeUpdate }, updateTeamBody);

      loadTeam(newTeam);
      queryClient.setQueryData(teamQueryKey, newTeam);

      return { teamBeforeUpdate };
    },
    onError: (error, updateTeamBody, context) => {
      Sentry.captureException(error);
      showToast({
        severity: "error",
        message:
          "Something went wrong when trying to apply the changes. Please try again or refresh the page.",
      });

      if (context?.teamBeforeUpdate && "settings" in context.teamBeforeUpdate) {
        loadTeam(context.teamBeforeUpdate);
        queryClient.setQueryData(
          ["workspaceTeam", updateTeamBody.teamId],
          context.teamBeforeUpdate
        );
      }
    },
  });

  const updateWorkspaceUserMutation = useMutation({
    mutationFn: async (user: PatchEnterpriseUserBody) =>
      await updateWorkspaceUser(currentWorkspaceId!, user),
    onMutate: async (newUser: PatchEnterpriseUserBody) => {
      await queryClient.cancelQueries({ queryKey: workspaceQueryKey });
      const previousWorkspace = queryClient.getQueryData(
        workspaceQueryKey
      ) as EnterpriseWorkspace;
      const workspaceWithUpdatedUser = {
        ...previousWorkspace,
        users: previousWorkspace.users.map((user) =>
          user.userId === newUser.userId ? { ...user, ...newUser } : user
        ),
      };
      queryClient.setQueryData(workspaceQueryKey, workspaceWithUpdatedUser);
      return { previousWorkspace };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: workspaceQueryKey }),
    onError: (error, _newUser, context) => {
      queryClient.setQueryData(workspaceQueryKey, context!.previousWorkspace);
      Sentry.captureException(error);
      showToast({
        severity: "error",
        message:
          "Something went wrong when updating the user. Please try again or refresh the page.",
      });
    },
  });

  const uploadCustomBotLogo = useMutation({
    mutationFn: async ({ file }: UploadLogoBody) =>
      await uploadCustomLogo(currentWorkspaceId!, file),
    onSuccess: (data, { onSuccess }) => {
      Events.changeWorkspaceSettings({ customBotLogoUri: "updated" });
      queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
      onSuccess();
    },
    onError: (error) => {
      Sentry.captureException(error);
      showToast({
        severity: "error",
        message:
          "Something went wrong when uploading your logo. Please try again or refresh the page.",
      });
    },
  });

  const deleteCustomBotLogo = useMutation({
    mutationFn: async () => await deleteCustomLogo(currentWorkspaceId!),
    onSuccess: () => {
      Events.changeWorkspaceSettings({ customBotLogoUri: "deleted" });
      return queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
    },
    onError: (error) => {
      Sentry.captureException(error);
      showToast({
        severity: "error",
        message:
          "Something went wrong when deleting your logo. Please try again or refresh the page.",
      });
    },
  });

  const getWorkspaceUser = () => {
    // TODO: Remove/replace with information returned
    // from user object after those changes are in
    let workspaceUser =
      workspace.users.find(
        (workspaceUser) => workspaceUser.userId === user?.id
      ) ?? DEFAULT_WORKSPACE_USER;

    // Add user's role to each of the user's teams
    workspaceUser = {
      ...workspaceUser,
      teams: (workspaceUser.teams ?? []).map((team) => {
        return {
          ...team,
          role: findMyRoleInTeam(team.users, workspaceUser.userId),
        };
      }),
    };

    return workspaceUser;
  };

  const workspaceContext: WorkspaceContext = {
    addOrUpdateTeamUsers: addOrUpdateTeamUsersMutation.mutate,
    createTeam: createTeamMutation.mutate,
    deleteTeam: deleteTeamMutation,
    deleteWorkspace: deleteWorkspaceMutation,
    migrateMeetings: migrateUserMeetingMutation,
    inWorkspace: currentWorkspaceId != null,
    leaveWorkspace: leaveWorkspaceMutation,
    loadTeam,
    loadedTeams: loadedTeams,
    removeUsersFromTeam: removeUsersFromTeamMutation,
    removeUsersFromWorkspace: removeWorkspaceUsersOrInvitesMutation.mutate,
    setWorkspaceId: setCurrentWorkspaceId,
    updateTeamName: updateTeamNameMutation.mutate,
    updateTeamSettings: updateTeamSettingMutation.mutate,
    updateTeamUsers: updateTeamUsersMutation.mutate,
    updateWorkspaceName: updateWorkspaceNameMutation.mutate,
    updateWorkspaceUser: updateWorkspaceUserMutation.mutate,
    uploadCustomBotLogo: uploadCustomBotLogo.mutate,
    deleteCustomBotLogo: deleteCustomBotLogo.mutate,
    workspace,
    workspaceIsInactive: workspace.status == "inactive",
    workspaceLoading: workspaceQuery.isLoading,
    workspaceError: workspaceQuery.isError,
    workspaceId: currentWorkspaceId,
    workspaceUser: getWorkspaceUser(),
  };

  const memoizedContext = useMemo(
    () => workspaceContext,
    [
      currentWorkspaceId,
      loadedTeams,
      workspaceQuery.isLoading,
      workspaceQuery.isError,
      workspaceQuery.isFetching,
      workspace.status,
      workspace.name,
      map("name", workspace.teams),
    ]
  );

  return (
    <WorkspaceContext.Provider value={memoizedContext}>
      {children}
    </WorkspaceContext.Provider>
  );
};
